import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PersonalInformation from '../components/PersonalInformation';
import ProductSelection from '../components/ProductSelection';
import { iPhone17Models } from '../components/data';
import { makePayment, initializePaystack } from '../actions/payment';
import '../styles/preorder-styles.css';

const Iphone17PreOrderForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    model: '',
    color: '',
    quantity: 1,
    terms: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const calculateTotal = () => {
    const preorderPrice = 25000;
    return preorderPrice * formData.quantity;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.model) newErrors.model = 'Please select an iPhone model';
    if (!formData.color) newErrors.color = 'Please select a color';
    if (!formData.terms)
      newErrors.terms = 'Please accept the terms and conditions';

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const phoneRegex = /^(\+234|0)[789][01]\d{8}$/;
    if (formData.phone && !phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid Nigerian phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const orderData = {
        amount: Number(calculateTotal()),
        account: formData.email,
        currency: process.env.REACT_APP_CURRENCY || 'NGN',
        processor: process.env.REACT_APP_PROCESSOR ,
        type: process.env.REACT_APP_TYPE || 'preorder',
        data: {
          orderId: `ORD-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          status: 'pending',
          customerInfo: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
          },
          productInfo: {
            model:
              iPhone17Models.find((m) => m.id === formData.model)?.name || '',
            color: formData.color,
            quantity: formData.quantity,
            preorderAmount: 25000,
          },
          orderType: 'iPhone 17 Pre-Order',
          totalAmount: calculateTotal(),
          orderDate: new Date().toISOString(),
        },
      };

      const result = await makePayment(orderData);

      if (!result || !result.data) {
        throw new Error('Payment initialization failed');
      }

      const payload = {
        email: formData.email,
        amount: result.data.amount,
        type: result.data.type,
        account: result.data.account,
        payment: result.data._id,
        metadata: {
          customer_name: `${formData.firstName} ${formData.lastName}`,
          model:
            iPhone17Models.find((m) => m.id === formData.model)?.name || '',
          order_type: 'iPhone 17 Pre-Order',
          phone: formData.phone,
          orderId: orderData.data.orderId, 
        },
      };

      const response = await initializePaystack(payload);

      if (response && response.success) {
        localStorage.setItem('orderId', orderData.data.orderId);
        window.location.href = response.data.data.authorization_url;
      } else {
        throw new Error('Payment initialization failed');
      }
    } catch (error) {
      console.error('Error initializing payment:', error);
      setIsLoading(false);
      if (
        error.message.includes('network') ||
        error.message.includes('fetch')
      ) {
        alert('Network error. Please check your connection and try again.');
      } else if (error.message.includes('validation')) {
        alert('Please check your order details and try again.');
      } else {
        alert('Something went wrong! Please try again.');
      }
    }
  };

  return (
    <div className='preorder-container'>
      <Header />
      <div className='preorder-form'>
        <PersonalInformation
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <ProductSelection
          formData={formData}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <div className='form-section'>
          <div className='checkbox-item'>
            <input
              type='checkbox'
              id='terms'
              name='terms'
              checked={formData.terms}
              onChange={handleInputChange}
              className={errors.terms ? 'error' : ''}
            />
            <label htmlFor='terms' className='checkbox-label'>
              I agree to the Terms & Conditions and Privacy Policy
            </label>
          </div>
          {errors.terms && <span className='error-text'>{errors.terms}</span>}
          <button
            type='button'
            className='submit-btn'
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? (
              <>
                <div className='loading-spinner'></div>
                Processing...
              </>
            ) : (
              <>💳 Complete Pre-Order (₦{calculateTotal().toLocaleString()})</>
            )}
          </button>
          <div className='payment-info'>
            <p>
              <span className='info-icon'>🛡️</span> Secure payment powered by
              Paystack. Your payment information is encrypted and secure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Iphone17PreOrderForm;
